import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const DocumentGrid = ({ 
  documents, 
  selectedDocuments, 
  onDocumentSelect, 
  onDocumentPreview, 
  onDocumentDelete,
  sortBy,
  sortOrder,
  onSort
}) => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFileIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf': return 'FileText';
      case 'jpg': case'jpeg': case'png': case'gif': return 'Image';
      case 'doc': case'docx': return 'FileText';
      case 'xls': case'xlsx': return 'FileSpreadsheet';
      default: return 'File';
    }
  };

  const getFileTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'pdf': return 'text-red-600';
      case 'jpg': case'jpeg': case'png': case'gif': return 'text-green-600';
      case 'doc': case'docx': return 'text-blue-600';
      case 'xls': case'xlsx': return 'text-emerald-600';
      default: return 'text-muted-foreground';
    }
  };

  const handleSelectAll = () => {
    if (selectedDocuments?.length === documents?.length) {
      onDocumentSelect([]);
    } else {
      onDocumentSelect(documents?.map(doc => doc?.id));
    }
  };

  const handleDocumentClick = (document, event) => {
    if (event?.ctrlKey || event?.metaKey) {
      // Multi-select with Ctrl/Cmd
      const newSelection = selectedDocuments?.includes(document?.id)
        ? selectedDocuments?.filter(id => id !== document?.id)
        : [...selectedDocuments, document?.id];
      onDocumentSelect(newSelection);
    } else {
      // Single select or preview
      onDocumentPreview(document);
    }
  };

  if (documents?.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-background">
        <div className="text-center">
          <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No documents found</h3>
          <p className="text-muted-foreground">Upload documents or adjust your search criteria</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-background">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-surface">
        <div className="flex items-center space-x-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={selectedDocuments?.length === documents?.length}
              onChange={handleSelectAll}
              className="rounded border-border"
            />
            <span className="text-sm text-muted-foreground">
              {selectedDocuments?.length > 0 
                ? `${selectedDocuments?.length} selected`
                : `${documents?.length} documents`
              }
            </span>
          </label>

          {selectedDocuments?.length > 0 && (
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Download">
                Download
              </Button>
              <Button variant="outline" size="sm" iconName="Trash2" onClick={() => onDocumentDelete(selectedDocuments)}>
                Delete
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <div className="flex items-center border border-border rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            >
              <Icon name="Grid3X3" size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            >
              <Icon name="List" size={16} />
            </button>
          </div>
        </div>
      </div>
      {/* Document Content */}
      <div className="p-4">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
            {documents?.map((document) => (
              <div
                key={document?.id}
                className={`relative group cursor-pointer rounded-lg border-2 transition-all duration-150 ${
                  selectedDocuments?.includes(document?.id)
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-surface'
                }`}
                onClick={(e) => handleDocumentClick(document, e)}
              >
                <div className="p-3">
                  {/* File Preview */}
                  <div className="aspect-square mb-3 flex items-center justify-center bg-muted rounded-lg">
                    {document?.thumbnail ? (
                      <Image
                        src={document?.thumbnail}
                        alt={document?.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <Icon
                        name={getFileIcon(document?.type)}
                        size={32}
                        className={getFileTypeColor(document?.type)}
                      />
                    )}
                  </div>

                  {/* File Info */}
                  <div className="space-y-1">
                    <h4 className="text-sm font-medium text-foreground truncate" title={document?.name}>
                      {document?.name}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(document?.size)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(document?.uploadDate)}
                    </p>
                  </div>
                </div>

                {/* Selection Checkbox */}
                <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <input
                    type="checkbox"
                    checked={selectedDocuments?.includes(document?.id)}
                    onChange={() => {}}
                    className="rounded border-border"
                    onClick={(e) => e?.stopPropagation()}
                  />
                </div>

                {/* Quick Actions */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      onDocumentPreview(document);
                    }}
                    className="p-1 bg-surface border border-border rounded hover:bg-muted"
                  >
                    <Icon name="Eye" size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {/* List Header */}
            <div className="grid grid-cols-12 gap-4 p-3 text-sm font-medium text-muted-foreground border-b border-border">
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={selectedDocuments?.length === documents?.length}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </div>
              <div className="col-span-5 cursor-pointer flex items-center space-x-1" onClick={() => onSort('name')}>
                <span>Name</span>
                {sortBy === 'name' && (
                  <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                )}
              </div>
              <div className="col-span-2 cursor-pointer flex items-center space-x-1" onClick={() => onSort('size')}>
                <span>Size</span>
                {sortBy === 'size' && (
                  <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                )}
              </div>
              <div className="col-span-2 cursor-pointer flex items-center space-x-1" onClick={() => onSort('uploadDate')}>
                <span>Modified</span>
                {sortBy === 'uploadDate' && (
                  <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
                )}
              </div>
              <div className="col-span-2">Actions</div>
            </div>

            {/* List Items */}
            {documents?.map((document) => (
              <div
                key={document?.id}
                className={`grid grid-cols-12 gap-4 p-3 rounded-lg cursor-pointer transition-colors duration-150 ${
                  selectedDocuments?.includes(document?.id)
                    ? 'bg-primary/5 border border-primary' :'hover:bg-muted/50 border border-transparent'
                }`}
                onClick={(e) => handleDocumentClick(document, e)}
              >
                <div className="col-span-1">
                  <input
                    type="checkbox"
                    checked={selectedDocuments?.includes(document?.id)}
                    onChange={() => {}}
                    className="rounded border-border"
                    onClick={(e) => e?.stopPropagation()}
                  />
                </div>
                <div className="col-span-5 flex items-center space-x-3">
                  <Icon
                    name={getFileIcon(document?.type)}
                    size={20}
                    className={getFileTypeColor(document?.type)}
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground">{document?.name}</p>
                    <p className="text-xs text-muted-foreground">{document?.type?.toUpperCase()}</p>
                  </div>
                </div>
                <div className="col-span-2 flex items-center">
                  <span className="text-sm text-muted-foreground">{formatFileSize(document?.size)}</span>
                </div>
                <div className="col-span-2 flex items-center">
                  <span className="text-sm text-muted-foreground">{formatDate(document?.uploadDate)}</span>
                </div>
                <div className="col-span-2 flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      onDocumentPreview(document);
                    }}
                    className="p-1 hover:bg-muted rounded"
                    title="Preview"
                  >
                    <Icon name="Eye" size={16} className="text-muted-foreground" />
                  </button>
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      // Handle download
                    }}
                    className="p-1 hover:bg-muted rounded"
                    title="Download"
                  >
                    <Icon name="Download" size={16} className="text-muted-foreground" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentGrid;